// =============================================================================
// OrderRegisterCard — the STOW / PO register on a project (and on a quote that
// sits on one). Mig 454 made the table, mig 462 feeds it.
// =============================================================================
// 27 Oct 2026. James: "the PO system way we see it in quotes is a bit shit".
// One card, both places:
//   - the money line: contract (= accepted quotes + variations, trigger-kept)
//     against what the client's orders cover, and for an RCTI client (Ventia)
//     how far each STOW has got: claimed → PE approved → RCTI received.
//   - one row per order: kind, number, deliverable, which quote it covers, the
//     amount, the paper. Click the next stage to stamp today.
//   - Add: PO or STOW, with the PDF; or make a one-line quote from an order
//     that arrived with no quote (app.quote_from_po).
// Legacy PO fields on the quote / project header flow in by trigger (462), so
// nothing typed in the old Client PO card is lost.
// 2 Nov 2026 (mig 490) — James, Cheongs Hill: the PO typed on Mark won landed
// on the register with "no quote linked · $0.00", and adding it again on the
// quote was refused as a duplicate. Mig 490 makes Mark won land it whole; here
// a number already on the register is not an error any more — the form loads
// that row and the save updates it (attaches the paper, fills the quote and
// the amount). The paper is a drop zone, and "View paper" is a visible link.
// =============================================================================
import React, { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, FileText, ExternalLink, Pencil, Trash2, CheckCircle2, Circle, ClipboardList, Receipt, X, AlertTriangle, Upload } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Button, Spinner } from "@/components/ui";
import { fmtMoney, fmtDate } from "@/lib/dates";
import { getClientPoDownloadUrl } from "./clientPoUpload";

type Kind = "po" | "stow";
interface OrderRow {
  order_id: string; project_id: string; quote_id: string | null; kind: Kind;
  order_number: string; deliverable: string | null; amount_ex_gst: number;
  received_date: string | null; claimed_at: string | null; pe_approved_at: string | null; rcti_received_at: string | null;
  rcti_reference: string | null; document_id: string | null; file_id: string | null; notes: string | null; is_active: boolean;
}
interface QuoteLite { quote_id: string; quote_number: string; quote_title: string | null; quote_type: string; status: string; subtotal_excl_gst: number }

const STAGES: { key: "claimed_at" | "pe_approved_at" | "rcti_received_at"; label: string; short: string }[] = [
  { key: "claimed_at",       label: "Claimed in NOVUS", short: "Claimed" },
  { key: "pe_approved_at",   label: "PE approved",      short: "PE" },
  { key: "rcti_received_at", label: "RCTI received",    short: "RCTI" },
];

const money = (n: number | null | undefined) => fmtMoney(Number(n ?? 0));
const todayIso = () => new Date().toISOString().slice(0, 10);

// -----------------------------------------------------------------------------
// The paper on an order: the PDF goes to bid-documents under the project with a
// bid_documents row, and the register row points at it. Shared by the Add /
// Edit form here and by Mark won (QuoteBuilderScreen), so a PDF dropped on the
// Mark-won dialog ends up on the same register row the trigger made.
// -----------------------------------------------------------------------------
export async function uploadOrderPaper(args: { projectId: string; quoteId: string | null; kind: Kind; orderNumber: string; deliverable?: string | null; file: File; userId: string | null }): Promise<string> {
  const { projectId, quoteId, kind, orderNumber, deliverable, file, userId } = args;
  const safe = file.name.replace(/[^A-Za-z0-9._-]/g, "_").slice(0, 80);
  const path = `project-${projectId}/orders/${Date.now()}-${safe}`;
  const { error: upErr } = await supabase.storage.from("bid-documents").upload(path, file, { contentType: file.type || "application/pdf", upsert: false });
  if (upErr) throw new Error(`Saving the PDF failed: ${upErr.message}`);
  // 3 Nov 2026 (mig 494) — onto the Commercial › Purchase Order shelf (managers only), never Uncategorised; the row is restricted by trigger regardless
  const { data: shelf } = await (supabase as any).from("document_subcategories").select("subcategory_id, main_category_id").ilike("display_name", "%purchase order%").eq("is_active", true).order("sort_order").limit(1).maybeSingle();
  const { data: doc, error: docErr } = await (supabase as any).from("bid_documents").insert({
    project_id: projectId, quote_id: quoteId, file_name: file.name, file_path: path, file_size_bytes: file.size,
    mime_type: file.type || "application/pdf", doc_type: "po",
    title: `${kind === "stow" ? "STOW" : "Client PO"} ${orderNumber}`, description: deliverable?.trim() || null, uploaded_by: userId,
    main_category_id: shelf?.main_category_id ?? null, subcategory_id: shelf?.subcategory_id ?? null,
  }).select("id").single();
  if (docErr) throw new Error(`Recording the PDF failed: ${docErr.message}`);
  return doc.id as string;
}

/** Put the paper (and a received date) on the register row that carries this number — the one Mark won / the trigger made. */
export async function attachPaperToRegisterRow(args: { projectId: string; kind: Kind; orderNumber: string; documentId: string; receivedDate?: string | null }): Promise<boolean> {
  const { projectId, kind, orderNumber, documentId, receivedDate } = args;
  const patch: any = { document_id: documentId }; if (receivedDate) patch.received_date = receivedDate;
  const { data, error } = await (supabase as any).from("quote_orders").update(patch)
    .eq("project_id", projectId).eq("kind", kind).eq("order_number", orderNumber.trim()).eq("is_active", true).select("order_id");
  if (error) throw new Error(error.message);
  return (data?.length ?? 0) > 0;
}

/** Drop-or-click PDF zone. */
export function PaperDrop({ file, onFile, existing, compact }: { file: File | null; onFile: (f: File | null) => void; existing?: string | null; compact?: boolean }) {
  const ref = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={e => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={e => { e.preventDefault(); setOver(false); const f = e.dataTransfer.files?.[0] ?? null; if (f) onFile(f); }}
      onClick={() => ref.current?.click()}
      className={`rounded-md border-2 border-dashed ${compact ? "px-2.5 py-2" : "px-3 py-2.5"} flex items-center gap-2.5 cursor-pointer transition-colors ${over ? "border-accent bg-accent/10" : file ? "border-ok/50 bg-ok/5" : "border-line hover:border-accent/60"}`}
    >
      <input ref={ref} type="file" accept="application/pdf" className="hidden" onChange={e => { onFile(e.target.files?.[0] ?? null); e.target.value = ""; }}/>
      {file ? <FileText size={15} className="text-ok"/> : <Upload size={15} className="text-text-muted"/>}
      <div className="min-w-0 flex-1 text-[12px]">
        {file ? <span className="text-text truncate block">{file.name} <span className="text-text-muted">· {(file.size / 1024).toFixed(0)} KB</span></span>
          : existing ? <span className="text-text-muted">{existing} — drop a PDF here to replace it</span>
          : <span className="text-text-muted">Drop the PO / STOW PDF here, or click to pick it</span>}
      </div>
      {file && <button type="button" className="text-text-muted hover:text-danger" title="Remove" onClick={e => { e.stopPropagation(); onFile(null); }}><X size={13}/></button>}
    </div>
  );
}

export default function OrderRegisterCard({ projectId, quoteId, compact }: { projectId: string; quoteId?: string; compact?: boolean }) {
  const qc = useQueryClient();
  const user = useRequireAuth();
  const canEdit = hasRole(user, "manager");
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["quote_orders", projectId],
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("quote_orders").select("*").eq("project_id", projectId).eq("is_active", true)
        .order("received_date", { ascending: true, nullsFirst: false }).order("created_at");
      if (error) throw error;
      return (data ?? []) as OrderRow[];
    },
  });
  const { data: pos } = useQuery({
    queryKey: ["claim_position", projectId],
    queryFn: async () => {
      const { data } = await (supabase as any).from("v_project_claim_position").select("*").eq("project_id", projectId).maybeSingle();
      return data as any | null;
    },
  });
  const { data: quotes = [] } = useQuery({
    queryKey: ["project_quotes_lite", projectId],
    queryFn: async () => {
      const { data } = await supabase.from("quotes").select("quote_id, quote_number, quote_title, quote_type, status, subtotal_excl_gst")
        .eq("project_id", projectId).in("status", ["sent", "accepted", "partially_invoiced", "invoiced"]).order("created_at");
      return (data ?? []) as QuoteLite[];
    },
  });
  const { data: project } = useQuery({
    queryKey: ["order_register_project", projectId],
    queryFn: async () => {
      const { data } = await (supabase as any).from("projects").select("project_id, contract_value, client:clients(client_id, legal_name, uses_rcti)").eq("project_id", projectId).maybeSingle();
      return data as any | null;
    },
  });
  const usesRcti: boolean = !!(project?.client?.uses_rcti ?? pos?.uses_rcti);
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["quote_orders", projectId] });
    qc.invalidateQueries({ queryKey: ["claim_position", projectId] });
    qc.invalidateQueries({ queryKey: ["project", projectId] });
    qc.invalidateQueries({ queryKey: ["order_register_project", projectId] });
  };

  const contract = Number(pos?.contract_value_ex ?? project?.contract_value ?? 0);
  const cover = useMemo(() => rows.reduce((s, r) => s + Number(r.amount_ex_gst ?? 0), 0), [rows]);
  const gap = Math.round((contract - cover) * 100) / 100;
  const byQuote = useMemo(() => Object.fromEntries(quotes.map(q => [q.quote_id, q])), [quotes]);
  const stageSum = (k: typeof STAGES[number]["key"]) => rows.filter(r => r.kind === "stow" && r[k]).reduce((s, r) => s + Number(r.amount_ex_gst), 0);

  const stamp = useMutation({
    mutationFn: async ({ id, key, value }: { id: string; key: string; value: string | null }) => {
      const patch: any = { [key]: value }; if (key === "claimed_at" && value) patch.claimed_by = user?.user_id ?? null;
      const { error } = await (supabase as any).from("quote_orders").update(patch).eq("order_id", id);
      if (error) throw error;
    },
    onSuccess: invalidate, onError: (e: any) => setErr(e?.message ?? String(e)),
  });
  // mig 494 — a PO that arrived with no quote behind it (off the intake / the header): one click makes the won quote and links the row
  const quoteFromRow = useMutation({
    mutationFn: async (id: string) => { const { error } = await (supabase as any).schema("app").rpc("quote_from_existing_po", { p_order_id: id }); if (error) throw new Error(error.message); },
    onSuccess: () => { invalidate(); qc.invalidateQueries({ queryKey: ["project_quotes_lite", projectId] }); }, onError: (e: any) => setErr(e?.message ?? String(e)),
  });
  const retire = useMutation({
    mutationFn: async (id: string) => { const { error } = await (supabase as any).from("quote_orders").update({ is_active: false }).eq("order_id", id); if (error) throw error; },
    onSuccess: invalidate, onError: (e: any) => setErr(e?.message ?? String(e)),
  });

  async function openPaper(r: OrderRow) {
    try {
      if (r.document_id) {
        const { data: d } = await (supabase as any).from("bid_documents").select("file_path").eq("id", r.document_id).maybeSingle();
        if (d?.file_path) { const { data: s } = await supabase.storage.from("bid-documents").createSignedUrl(d.file_path, 600); if (s?.signedUrl) { window.open(s.signedUrl, "_blank", "noopener"); return; } }
      }
      if (r.file_id) {
        const { data: f } = await supabase.from("files").select("r2_object_key").eq("file_id", r.file_id).maybeSingle();
        if (f?.r2_object_key) { window.open(await getClientPoDownloadUrl(f.r2_object_key), "_blank", "noopener"); return; }
      }
      setErr("No paper saved on that order yet.");
    } catch (e: any) { setErr(e?.message ?? String(e)); }
  }

  const mine = (r: OrderRow) => !!quoteId && r.quote_id === quoteId;
  const kindPill = (k: Kind) => (
    <span className={`text-[9.5px] font-mono uppercase px-1.5 py-0.5 rounded border ${k === "stow" ? "bg-warn/10 text-warn border-warn/30" : "bg-accent/10 text-accent border-accent/30"}`}>{k}</span>
  );

  return (
    <Card>
      <div className="px-4 py-3 border-b border-line flex items-center gap-2 flex-wrap">
        <ClipboardList size={14} className="text-accent"/>
        <span className="text-[11px] uppercase tracking-wider2 text-text-muted font-medium">{usesRcti ? "STOW / PO register" : "Client PO register"}</span>
        {project?.client?.legal_name && <span className="text-[11px] text-text-dim">· {project.client.legal_name}{usesRcti ? " · RCTI" : ""}</span>}
        <span className="flex-1"/>
        {canEdit && !adding && <Button size="sm" variant="secondary" icon={Plus} onClick={() => { setAdding(true); setEditId(null); }}>{usesRcti ? "Add a STOW / PO" : "Add a PO"}</Button>}
      </div>

      {/* 28 Oct 2026 — James: "this PO register is confusing in the quote". On a quote
          (compact) it is just the orders that cover THIS quote; the contract /
          cover / claim dashboard lives on the project. */}
      {compact && (
        <div className="px-4 py-2 text-[12px] text-text-muted">
          {rows.filter(mine).length === 0
            ? <>No client PO covers this quote yet{usesRcti ? " — add the STOW when NOVUS raises it" : " — add the PO when it lands"}.</>
            : <>{rows.filter(mine).length === 1 ? "One client order covers this quote" : `${rows.filter(mine).length} client orders cover this quote`} · <span className="font-mono text-text">{money(rows.filter(mine).reduce((a, r) => a + Number(r.amount_ex_gst ?? 0), 0))}</span>. The full register is on the project's Financials tab.</>}
        </div>
      )}
      {/* the money line */}
      {!compact && <div className={`px-4 py-3 grid gap-2 ${usesRcti ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6" : "grid-cols-2 sm:grid-cols-4"}`}>
        <Tile label="Contract" value={money(contract)} sub="accepted quotes + variations"/>
        <Tile label={usesRcti ? "STOW / PO cover" : "PO cover"} value={money(cover)} sub={`${rows.length} order${rows.length === 1 ? "" : "s"}`}/>
        <Tile label={gap > 0 ? "Not yet covered" : gap < 0 ? "Orders over contract" : "Covered"} value={gap === 0 ? "✓" : money(Math.abs(gap))}
          tone={gap === 0 ? "ok" : gap > 0 ? "warn" : "info"} sub={gap > 0 ? "waiting on paper" : gap < 0 ? "quote the difference?" : "paper matches the quotes"}/>
        {usesRcti ? (
          <>
            <Tile label="Claimed" value={money(stageSum("claimed_at"))} sub="in NOVUS"/>
            <Tile label="PE approved" value={money(stageSum("pe_approved_at"))}/>
            <Tile label="RCTI in" value={money(stageSum("rcti_received_at"))} tone="ok"/>
          </>
        ) : (
          <Tile label="Invoiced" value={money(pos?.invoiced_ex ?? 0)} sub={pos ? `${money(pos.paid_ex)} paid` : undefined}/>
        )}
      </div>}
      {contract > 0 && !compact && (
        <div className="px-4 pb-3">
          <div className="h-1.5 rounded-full bg-ink-raised overflow-hidden flex">
            <div className={`h-full ${cover >= contract ? "bg-ok" : "bg-accent"}`} style={{ width: `${Math.min(100, (cover / contract) * 100)}%` }}/>
          </div>
          <div className="text-[10px] font-mono text-text-dim mt-1 text-right">{contract > 0 ? Math.round((cover / contract) * 100) : 0}% of the contract has an order behind it</div>
        </div>
      )}

      {err && <div className="mx-4 mb-2 text-[12px] text-danger flex items-center gap-2"><AlertTriangle size={13}/>{err}<button className="ml-auto" onClick={() => setErr(null)}><X size={12}/></button></div>}

      {/* the rows */}
      {isLoading ? <div className="p-4 flex justify-center"><Spinner/></div> : rows.length === 0 && !adding ? (
        <div className="px-4 pb-4 text-[12.5px] text-text-muted">No orders recorded yet. {usesRcti ? "Add each STOW as NOVUS raises it — the claim steps live here." : "Add the client's PO when it lands; a PO saved on the quote shows here by itself."}</div>
      ) : (
        <div className="divide-y divide-line border-t border-line">
          {/* on a quote: its own orders, plus any order on the project with no quote linked — so the one Mark won made is in view to fix */}
          {rows.filter(r => !compact || mine(r) || !r.quote_id).map(r => editId === r.order_id ? (
            <OrderForm key={r.order_id} projectId={projectId} quotes={quotes} usesRcti={usesRcti} initial={r} defaultQuoteId={quoteId} existing={rows}
              onClose={() => setEditId(null)} onSaved={() => { setEditId(null); invalidate(); }}/>
          ) : (
            <div key={r.order_id} className={`px-4 py-2.5 ${mine(r) ? "bg-accent/[0.04]" : ""}`}>
              <div className="flex items-center gap-3 flex-wrap">
                {kindPill(r.kind)}
                <span className="font-mono text-[13px]">{r.order_number}</span>
                {r.deliverable && <span className="text-[12.5px] text-text-muted truncate max-w-[320px]" title={r.deliverable}>{r.deliverable}</span>}
                {r.quote_id && byQuote[r.quote_id] && (
                  <Link to={`/quotes/${r.quote_id}`} className="text-[11px] font-mono text-accent hover:underline inline-flex items-center gap-1" title={byQuote[r.quote_id].quote_title ?? ""}>
                    covers {byQuote[r.quote_id].quote_number}{byQuote[r.quote_id].quote_type === "variation" ? " (var)" : ""} <ExternalLink size={10}/>
                  </Link>
                )}
                {!r.quote_id && (canEdit
                  ? <button type="button" className="text-[11px] text-warn hover:underline inline-flex items-center gap-1" title="No quote was issued for this order — make a one-line won quote for its amount so the contract value and budget line up"
                      onClick={() => { if (confirm(`Make a one-line won quote for ${r.kind.toUpperCase()} ${r.order_number} at ${money(r.amount_ex_gst)} and link it? The contract value follows.`)) quoteFromRow.mutate(r.order_id); }}>
                      <Receipt size={11}/>no quote — make one from this {r.kind.toUpperCase()}</button>
                  : <span className="text-[11px] text-text-dim">no quote linked</span>)}
                <span className="flex-1"/>
                <span className="font-mono text-[13px]">{money(r.amount_ex_gst)}</span>
                <span className="text-[11px] text-text-dim w-[84px] text-right">{r.received_date ? fmtDate(r.received_date) : "—"}</span>
                {(r.document_id || r.file_id) ? (
                  <button className="inline-flex items-center gap-1 text-[11px] text-accent hover:underline" title="Open the PO / STOW paper" onClick={() => openPaper(r)}><FileText size={13}/>View paper</button>
                ) : canEdit ? (
                  <button className="inline-flex items-center gap-1 text-[11px] text-warn hover:underline" title="No paper saved — add the PDF" onClick={() => { setEditId(r.order_id); setAdding(false); }}><Upload size={12}/>No paper</button>
                ) : <span className="text-[11px] text-text-dim">No paper</span>}
                {canEdit && <button className="text-text-muted hover:text-accent" title="Edit" onClick={() => { setEditId(r.order_id); setAdding(false); }}><Pencil size={13}/></button>}
                {canEdit && <button className="text-text-muted hover:text-danger" title="Remove from the register" onClick={() => { if (confirm(`Remove ${r.kind.toUpperCase()} ${r.order_number} from the register?`)) retire.mutate(r.order_id); }}><Trash2 size={13}/></button>}
              </div>
              {(usesRcti || r.kind === "stow") && (
                <div className="mt-1.5 flex items-center gap-1 text-[11px]">
                  <StageDot done label="Received" date={r.received_date}/>
                  {STAGES.map((st, i) => {
                    const done = !!r[st.key]; const prevDone = i === 0 ? true : !!r[STAGES[i - 1].key];
                    return (
                      <React.Fragment key={st.key}>
                        <span className={`h-px w-4 ${done ? "bg-ok" : "bg-line"}`}/>
                        <StageDot done={done} label={st.label} short={st.short} date={r[st.key]} next={!done && prevDone && canEdit}
                          onStamp={() => stamp.mutate({ id: r.order_id, key: st.key, value: todayIso() })}
                          onClear={done && canEdit ? () => { if (confirm(`Clear "${st.label}" on ${r.order_number}?`)) stamp.mutate({ id: r.order_id, key: st.key, value: null }); } : undefined}/>
                      </React.Fragment>
                    );
                  })}
                  {r.rcti_reference && <span className="ml-2 font-mono text-text-dim">RCTI {r.rcti_reference}</span>}
                </div>
              )}
              {r.notes && !compact && <div className="mt-1 text-[11.5px] text-text-dim">{r.notes}</div>}
            </div>
          ))}
        </div>
      )}

      {adding && (
        <div className="border-t border-line">
          <OrderForm projectId={projectId} quotes={quotes} usesRcti={usesRcti} defaultQuoteId={quoteId} existing={rows} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); invalidate(); }}/>
        </div>
      )}
    </Card>
  );
}

function Tile({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: "ok" | "warn" | "info" }) {
  const bg = tone === "ok" ? "bg-ok/10" : tone === "warn" ? "bg-warn/10" : tone === "info" ? "bg-info/10" : "bg-ink-raised";
  return (
    <div className={`rounded-lg p-2.5 ${bg}`}>
      <div className="text-[10px] uppercase tracking-wider2 text-text-muted">{label}</div>
      <div className="font-mono text-[14px] mt-0.5">{value}</div>
      {sub && <div className="text-[10.5px] text-text-dim truncate">{sub}</div>}
    </div>
  );
}

function StageDot({ done, label, short, date, next, onStamp, onClear }: { done: boolean; label: string; short?: string; date?: string | null; next?: boolean; onStamp?: () => void; onClear?: () => void }) {
  const text = short ?? label;
  if (done) return (
    <button type="button" className="inline-flex items-center gap-1 text-ok" title={`${label}${date ? ` · ${fmtDate(date)}` : ""}${onClear ? " — click to clear" : ""}`} onClick={onClear} disabled={!onClear}>
      <CheckCircle2 size={12}/>{text}{date && <span className="text-text-dim font-mono">{fmtDate(date).replace(/\s\d{4}$/, "")}</span>}
    </button>
  );
  if (next) return (
    <button type="button" className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-dashed border-accent/50 text-accent hover:bg-accent/10" title={`Mark "${label}" today`} onClick={onStamp}>
      <Circle size={11}/>{text}
    </button>
  );
  return <span className="inline-flex items-center gap-1 text-text-dim" title={label}><Circle size={11}/>{text}</span>;
}

// -----------------------------------------------------------------------------
// Add / edit one order
// -----------------------------------------------------------------------------
function OrderForm({ projectId, quotes, usesRcti, initial, defaultQuoteId, existing, onClose, onSaved }: {
  projectId: string; quotes: QuoteLite[]; usesRcti: boolean; initial?: OrderRow; defaultQuoteId?: string; existing: OrderRow[]; onClose: () => void; onSaved: () => void;
}) {
  const user = useRequireAuth();
  const [kind, setKind] = useState<Kind>(initial?.kind ?? (usesRcti ? "stow" : "po"));
  const [number, setNumber] = useState(initial?.order_number ?? "");
  const [deliverable, setDeliverable] = useState(initial?.deliverable ?? "");
  const [amount, setAmount] = useState(initial ? String(initial.amount_ex_gst) : "");
  const [received, setReceived] = useState(initial?.received_date ?? todayIso());
  const [quoteSel, setQuoteSel] = useState(initial?.quote_id ?? defaultQuoteId ?? "");
  const [rctiRef, setRctiRef] = useState(initial?.rcti_reference ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [file, setFile] = useState<File | null>(null);
  const [makeQuote, setMakeQuote] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const amt = Number(amount);
  const ok = number.trim().length > 0 && Number.isFinite(amt) && amt >= 0 && (!makeQuote || amt > 0);
  // mig 490 — the same number already on this project's register is the row to
  // update, not a duplicate to refuse (Mark won made it; this attaches the paper).
  const twin = !initial ? existing.find(r => r.order_number === number.trim() && r.kind === kind) ?? null : null;

  async function save() {
    if (!ok) return; setSaving(true); setErr(null);
    try {
      let document_id: string | null = initial?.document_id ?? twin?.document_id ?? null;
      if (file) {
        document_id = await uploadOrderPaper({ projectId, quoteId: quoteSel || null, kind, orderNumber: number.trim(), deliverable, file, userId: user?.user_id ?? null });
      }
      if (!initial && makeQuote) {
        // a job that arrived as an order with no quote: one-line quote, accepted, onto the project, register row made by the function
        const { error } = await (supabase as any).schema("app").rpc("quote_from_po", { p_project_id: projectId, p_order_number: number.trim(), p_amount_ex: amt, p_kind: kind, p_deliverable: deliverable.trim() || null, p_received: received || null, p_document_id: document_id });
        if (error) throw new Error(error.message);
      } else {
        const row: any = {
          project_id: projectId, quote_id: quoteSel || null, kind, order_number: number.trim(), deliverable: deliverable.trim() || null,
          amount_ex_gst: amt, received_date: received || null, rcti_reference: rctiRef.trim() || null, notes: notes.trim() || null, document_id,
        };
        if (initial) {
          const { error } = await (supabase as any).from("quote_orders").update({ ...row, updated_by: user?.user_id ?? null }).eq("order_id", initial.order_id);
          if (error) throw new Error(error.message);
        } else if (twin) {
          // the row Mark won (or the header) made: fill what it is missing, keep what it has
          const patch: any = {
            quote_id: quoteSel || twin.quote_id, deliverable: row.deliverable ?? twin.deliverable,
            amount_ex_gst: amt > 0 ? amt : twin.amount_ex_gst, received_date: row.received_date ?? twin.received_date,
            rcti_reference: row.rcti_reference ?? twin.rcti_reference, notes: row.notes ?? twin.notes, document_id: document_id ?? twin.document_id,
            updated_by: user?.user_id ?? null,
          };
          const { error } = await (supabase as any).from("quote_orders").update(patch).eq("order_id", twin.order_id);
          if (error) throw new Error(error.message);
        } else {
          const { error } = await (supabase as any).from("quote_orders").insert({ ...row, created_by: user?.user_id ?? null });
          if (error) throw new Error(/ux_quote_orders_project_number/.test(error.message) ? `${kind.toUpperCase()} ${number.trim()} is already on this project's register — it is now loaded above; save again to update it.` : error.message);
        }
      }
      onSaved();
    } catch (e: any) { setErr(e?.message ?? String(e)); setSaving(false); }
  }

  // when the typed number hits a row already on the register, show that row's values so the save reads as an update
  const twinId = twin?.order_id ?? null;
  React.useEffect(() => {
    if (!twin) return;
    if (!amount || Number(amount) === 0) setAmount(Number(twin.amount_ex_gst) > 0 ? String(twin.amount_ex_gst) : "");
    if (!deliverable && twin.deliverable) setDeliverable(twin.deliverable);
    if (!quoteSel && twin.quote_id) setQuoteSel(twin.quote_id);
    if (twin.received_date) setReceived(twin.received_date);
    if (!notes && twin.notes) setNotes(twin.notes);
    setMakeQuote(false);
  }, [twinId]);   // eslint-disable-line react-hooks/exhaustive-deps

  const inp = "w-full bg-ink-raised border border-line rounded-md px-2.5 py-1.5 text-[13px] outline-none focus:border-accent";
  const lbl = "text-[10px] uppercase tracking-wider2 text-text-muted block mb-1";
  return (
    <div className="p-4 bg-ink-raised/40">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[12.5px] font-medium">{initial ? `Edit ${initial.kind.toUpperCase()} ${initial.order_number}` : twin ? `${twin.kind.toUpperCase()} ${twin.order_number} is on the register — update it` : "Record an order from the client"}</span>
        <span className="flex-1"/>
        <button className="text-text-muted hover:text-text" onClick={onClose}><X size={15}/></button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <span className={lbl}>What is it</span>
          <div className="flex gap-1">
            {(["po", "stow"] as Kind[]).map(k => (
              <button key={k} type="button" onClick={() => setKind(k)} className={`flex-1 px-2 py-1.5 rounded-md border text-[12px] font-mono uppercase ${kind === k ? (k === "stow" ? "border-warn bg-warn/10 text-warn" : "border-accent bg-accent/10 text-accent") : "border-line text-text-muted"}`}>{k}</button>
            ))}
          </div>
        </div>
        <div><span className={lbl}>{kind === "stow" ? "NOVUS number" : "PO number"}</span><input className={`${inp} font-mono`} value={number} onChange={e => setNumber(e.target.value)} placeholder={kind === "stow" ? "e.g. 710490" : "e.g. 4500123456"}/></div>
        <div><span className={lbl}>Amount ex GST</span><input className={`${inp} font-mono`} type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00"/></div>
        <div><span className={lbl}>{kind === "stow" ? "Raised" : "Received"}</span><input className={inp} type="date" value={received} onChange={e => setReceived(e.target.value)}/></div>
        <div className="col-span-2"><span className={lbl}>{kind === "stow" ? "Deliverable" : "What it is for"}</span><input className={inp} value={deliverable} onChange={e => setDeliverable(e.target.value)} placeholder={kind === "stow" ? "the NOVUS deliverable line" : "the PO description"}/></div>
        <div className="col-span-2">
          <span className={lbl}>Covers which quote</span>
          <select className={inp} value={makeQuote ? "" : quoteSel} onChange={e => setQuoteSel(e.target.value)} disabled={makeQuote}>
            <option value="">— not linked to a quote —</option>
            {quotes.map(q => <option key={q.quote_id} value={q.quote_id}>{q.quote_number}{q.quote_type === "variation" ? " (variation)" : ""} · {money(q.subtotal_excl_gst)}{q.quote_title ? ` · ${q.quote_title}` : ""}</option>)}
          </select>
        </div>
        {kind === "stow" && initial && <div className="col-span-2"><span className={lbl}>RCTI reference</span><input className={`${inp} font-mono`} value={rctiRef} onChange={e => setRctiRef(e.target.value)} placeholder="Xero / RCTI number once it lands"/></div>}
        <div className="col-span-2"><span className={lbl}>Paper (PDF)</span><PaperDrop file={file} onFile={setFile} compact existing={(initial ?? twin)?.document_id || (initial ?? twin)?.file_id ? "A file is saved already" : null}/></div>
        <div className="col-span-2 md:col-span-4"><span className={lbl}>Notes</span><input className={inp} value={notes} onChange={e => setNotes(e.target.value)} placeholder="anything the next person should know"/></div>
      </div>
      {twin && (
        <div className="mt-2 text-[11.5px] text-text-muted">This number is already on the register{twin.quote_id ? "" : " with no quote linked"}{Number(twin.amount_ex_gst) > 0 ? "" : " and no amount"}. Saving fills in what it is missing and attaches the paper — nothing is duplicated.</div>
      )}
      {!initial && !twin && (
        <label className="mt-3 flex items-start gap-2 text-[12px] cursor-pointer">
          <input type="checkbox" className="mt-0.5" checked={makeQuote} onChange={e => setMakeQuote(e.target.checked)}/>
          <span><span className="font-medium">No quote was issued — make a one-line quote from this order.</span> <span className="text-text-muted">The job arrived as a {kind.toUpperCase()}. This writes an accepted one-line quote for the amount onto the project so the contract value and budget line up, and links this order to it.</span></span>
        </label>
      )}
      {err && <div className="mt-2 text-[12px] text-danger flex items-center gap-1.5"><AlertTriangle size={13}/>{err}</div>}
      <div className="mt-3 flex gap-2 justify-end">
        <Button size="sm" variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button size="sm" icon={initial ? CheckCircle2 : Receipt} disabled={!ok || saving} onClick={save}>{saving ? "Saving…" : initial ? "Save" : twin ? "Update it" : makeQuote ? "Record and make the quote" : "Record it"}</Button>
      </div>
    </div>
  );
}
